/**
 * The two auth-owned seams the RTK Query identity machinery runs on — the token-identity CLOCK and the identity
 * COMMIT predicate. Both are INJECTED here, never imported.
 *
 * `@pressreader/services` owns the clock (`svcAuth.identityKey()` / `svcAuth.identityGeneration()`), but
 * appstore cannot import it: `appstore -> services -> config -> appstore` is a project cycle (rejected by
 * both `import/no-cycle` and nx's circular-dependency check) — the same reason `queryFnFromService`
 * re-declares the `RequestConfig` slice it needs. The commit predicate is the same problem in a different
 * shape: deciding that the app has COMMITTED an identity change means reading the auth slice, which appstore
 * cannot import either. `@pressreader/authentication-core`, which may import both, wires the two in during auth
 * bootstrap via `setIdentityClock` and `setIdentityCommitPredicate`.
 *
 * This module is the SEAM ONLY — two slots, their setters, and the raw reads its two sibling modules need. That
 * is deliberate: the auth bootstrap that injects is EAGER, so everything reachable from it is on the eager path,
 * while every CONSUMER of the clock is lazy (a fenced queryFn, the policy registry). The reading half therefore
 * lives in `./identity.snapshot` — see there.
 */

/**
 * The clock the identity fence reads.
 *
 * It is a GENERATION counter, not just a key: it bumps once per identity transition, so A -> B -> A is still a
 * change (key equality alone would read it as "unchanged" and let a stale write through).
 */
export interface IdentityClock {
    /** Normalized identity key of the CURRENT token: the user key when signed in, else the anonymous key. */
    key(): string;
    /** Monotonic counter that bumps ONLY when `key()` changes — the value the fence compares. */
    generation(): number;
    /**
     * The `key()` value that means "signed out" (`ANONYMOUS_IDENTITY_KEY` in `@pressreader/services`) — all
     * `QueryIdentity.isAuthenticated` needs. REQUIRED: appstore cannot import the constant (see above), and a
     * local default would be a second copy of the same literal, free to drift from the one that matters.
     */
    anonymousKey: string;
}

let clock: IdentityClock | undefined;

/**
 * Wire the identity clock. Called once from `@pressreader/authentication-core`'s bootstrap, before any
 * feature can dispatch a query. Idempotent — a later call replaces the clock.
 */
export function setIdentityClock(identityClock: IdentityClock): void {
    clock = identityClock;
}

/**
 * The raw slot, for `./identity.snapshot` — which owns every READING of the clock, including the fail-open
 * placeholder and the single report for a clock nobody wired. Deliberately not on the barrel: a consumer wants
 * `readIdentity()`, never the slot.
 */
export function getIdentityClock(): IdentityClock | undefined {
    return clock;
}

/**
 * Whether the app has COMMITTED an identity change — an RTK listener predicate, so it is handed the state from
 * before AND after the action was reduced and can read the transition straight off that pair (no module-level
 * "last identity" to go stale). Declared with `unknown` parameters because appstore knows nothing about the auth
 * slice the real predicate reads.
 */
export type IdentityCommitPredicate = (action: unknown, currentState: unknown, originalState: unknown) => boolean;

let commitPredicate: IdentityCommitPredicate | undefined;

/** The one deferred arming waiting for the injection — see `notifyOnIdentityCommitPredicateSet`. */
let onCommitPredicateSet: (() => void) | undefined;

/**
 * Wire the identity commit predicate, from the same `@pressreader/authentication-core` bootstrap that wires the
 * clock. Consumed by `armIdentityCommitReconcile` in `./identity.policy`, which is what registers the app-wide
 * commit reconcile listener with it — so injecting is all auth-core has to do for the reconcile to run.
 * Idempotent — a later call replaces the predicate, but a listener already armed keeps the one it was armed with.
 *
 * An arming that ran before this call had nothing to arm WITH; it is completed here, so injecting is enough
 * whichever of the two happens first (see `notifyOnIdentityCommitPredicateSet`).
 */
export function setIdentityCommitPredicate(predicate: IdentityCommitPredicate): void {
    commitPredicate = predicate;
    onCommitPredicateSet?.();
}

/** The raw slot, for `armIdentityCommitReconcile` (`./identity.policy`). Not on the barrel. */
export function getIdentityCommitPredicate(): IdentityCommitPredicate | undefined {
    return commitPredicate;
}

/**
 * Let a DEFERRED commit-listener arming complete the moment the injection happens.
 *
 * `armIdentityCommitReconcile` (`./identity.policy`) can do nothing while no predicate is injected; instead of
 * waiting for a further declaration that may never come, it leaves itself here and the injection finishes the job.
 * A single slot: there is exactly one such arming in the app and it is idempotent. Internal to appstore — not on
 * the barrel, since nothing outside this lib has an arming to defer.
 */
export function notifyOnIdentityCommitPredicateSet(listener: () => void): void {
    onCommitPredicateSet = listener;
}

/**
 * Test-only seam: clear BOTH slots — the injected predicate and the deferred-arming listener — so a case can drive
 * the un-injected state (and the retry out of it) rather than inheriting whatever an earlier case injected. Not on
 * the barrel; specs import it by relative path. Never call it from production code.
 */
export function __resetIdentityCommitPredicateForTests(): void {
    commitPredicate = undefined;
    onCommitPredicateSet = undefined;
}
